import { useRef } from "react";
import { motion, useMotionValue, useSpring, useScroll, useTransform } from "framer-motion";
import { ChevronDown, ArrowRight } from "lucide-react";
import { KineticText } from "@/components/KineticText";
import { useConsultation } from "@/context/ConsultationContext";

const HERO_BG =
  "https://static.prod-images.emergentagent.com/jobs/7c00204c-79e5-4008-ab5a-b17f868c095e/images/fda9379b3fc83331fcfb5dc8e7146dfc6072bffed01055d8ede16409f6c59879.jpeg";

// Arc control points in the SVG's coordinate space (viewBox 0 0 1000 500)
const P0 = [140, 340]; // India
const C = [500, 70]; // apex
const P1 = [860, 300]; // Portugal
const qbez = (t, a, b, c) => (1 - t) * (1 - t) * a + 2 * (1 - t) * t * b + t * t * c;
const qder = (t, a, b, c) => 2 * (1 - t) * (b - a) + 2 * t * (c - b);

// Cinematic India -> Portugal hero. The plane advances along the flight arc as
// the visitor scrolls (Framer Motion scroll progress), with a gold trail, a
// scroll-driven camera pan + 3D tilt, and mouse-parallax depth. Warm palette, no blue.
export function SceneJourney() {
  const { openConsultation } = useConsultation();
  const ref = useRef(null);

  // ---- scroll progress across the hero ----
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const t = useTransform(scrollYProgress, [0, 0.9], [0, 1], { clamp: true });

  // plane position + heading along the bezier
  const px = useTransform(t, (v) => qbez(v, P0[0], C[0], P1[0]));
  const py = useTransform(t, (v) => qbez(v, P0[1], C[1], P1[1]));
  const angle = useTransform(t, (v) => (Math.atan2(qder(v, P0[1], C[1], P1[1]), qder(v, P0[0], C[0], P1[0])) * 180) / Math.PI);
  const trailOffset = useTransform(t, [0, 1], [1, 0]); // draws the gold trail behind the plane
  const ptGlow = useTransform(t, [0.78, 1], [0, 1]); // Portugal pin lights up on arrival

  // scroll-driven camera pan + 3D tilt (the "transition" from India toward Portugal)
  const camX = useTransform(scrollYProgress, [0, 1], ["4%", "-7%"]);
  const camScale = useTransform(scrollYProgress, [0, 1], [1.12, 1.22]);
  const camRotX = useTransform(scrollYProgress, [0, 1], [0, 5]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, -70]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  // ---- mouse parallax depth ----
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const smx = useSpring(mx, { stiffness: 60, damping: 18 });
  const smy = useSpring(my, { stiffness: 60, damping: 18 });
  const bgX = useTransform(smx, [-1, 1], [-16, 16]);
  const bgY = useTransform(smy, [-1, 1], [-10, 10]);
  const arcX = useTransform(smx, [-1, 1], [26, -26]);
  const txtX = useTransform(smx, [-1, 1], [-9, 9]);

  const onMouse = (e) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    mx.set(((e.clientX - r.left) / r.width) * 2 - 1);
    my.set(((e.clientY - r.top) / r.height) * 2 - 1);
  };

  return (
    <section
      ref={ref}
      onMouseMove={onMouse}
      data-testid="scene-dream"
      className="relative min-h-screen overflow-hidden flex flex-col items-center justify-center"
      style={{ perspective: 1400 }}
    >
      {/* Background illustration: scroll camera-pan/tilt (outer) + mouse parallax (inner) */}
      <motion.div style={{ x: camX, scale: camScale, rotateX: camRotX }} className="absolute inset-0 z-0 origin-bottom">
        <motion.div style={{ x: bgX, y: bgY }} className="absolute inset-0">
          <img src={HERO_BG} alt="A journey from India to Portugal — India Gate to the Belém Tower and Lisbon at golden hour" className="h-full w-full object-cover scale-110" />
          <div className="absolute inset-0 bg-gradient-to-b from-maroon-deep/90 via-maroon-deep/45 to-maroon-deep/90" />
          <div className="absolute inset-0 bg-gradient-to-t from-maroon-deep via-transparent to-transparent" />
        </motion.div>
      </motion.div>

      {/* central scrim for legibility */}
      <div className="absolute inset-0 z-[15] pointer-events-none" style={{ background: "radial-gradient(62% 62% at 50% 44%, rgba(52,10,20,0.92) 0%, rgba(52,10,20,0.6) 58%, rgba(52,10,20,0) 84%)" }} />

      {/* Flight path + scroll-driven plane */}
      <motion.div style={{ x: arcX }} className="absolute inset-0 z-10 pointer-events-none">
        <svg viewBox="0 0 1000 500" preserveAspectRatio="xMidYMid slice" className="h-full w-full">
          <defs>
            <linearGradient id="arcg" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#C6A15B" stopOpacity="0.15" />
              <stop offset="50%" stopColor="#E0C486" stopOpacity="0.7" />
              <stop offset="100%" stopColor="#C6A15B" stopOpacity="0.15" />
            </linearGradient>
          </defs>

          {/* faint full arc */}
          <path d="M140,340 Q500,70 860,300" fill="none" stroke="url(#arcg)" strokeWidth="2" strokeDasharray="2 9" strokeLinecap="round" opacity="0.55" />
          {/* gold trail that draws as the plane advances */}
          <motion.path d="M140,340 Q500,70 860,300" fill="none" stroke="#E0C486" strokeWidth="3" strokeLinecap="round" pathLength={1} strokeDasharray={1} style={{ strokeDashoffset: trailOffset }} />

          {/* India endpoint */}
          <g>
            <circle cx="140" cy="340" r="7" fill="#A82A44" />
            <text x="140" y="372" textAnchor="middle" fill="#F7F1E8" fontSize="17" fontFamily="Syne, sans-serif" letterSpacing="3" fontWeight="700">INDIA</text>
          </g>
          {/* Portugal endpoint (lights up on arrival) */}
          <g>
            <motion.circle cx="860" cy="300" r="16" fill="#E0C486" style={{ opacity: ptGlow }} />
            <circle cx="860" cy="300" r="7" fill="#C6A15B" />
            <text x="860" y="332" textAnchor="middle" fill="#F7F1E8" fontSize="17" fontFamily="Syne, sans-serif" letterSpacing="3" fontWeight="700">PORTUGAL</text>
          </g>

          {/* plane — position/heading tied to scroll */}
          <motion.g style={{ x: px, y: py, rotate: angle, transformBox: "fill-box", transformOrigin: "center" }}>
            <path d="M20,0 L-16,-13 L-6,0 L-16,13 Z" fill="#F7F1E8" />
          </motion.g>
        </svg>
      </motion.div>

      {/* Headline + CTA */}
      <motion.div style={{ x: txtX, y: heroY, opacity: heroOpacity }} className="relative z-20 text-center px-6 max-w-5xl">
        <p className="accent-eyebrow text-gold">Effizent Europe · Portugal Partner</p>
        <KineticText as="h1" text="From an idea in India" className="font-playfair text-4xl md:text-6xl lg:text-7xl mt-6 leading-[1.0] tracking-tight text-cream font-semibold" delay={0.4} onMount />
        <KineticText as="h1" text="to your future in Portugal." className="font-playfair italic text-gold-soft text-4xl md:text-6xl lg:text-7xl leading-[1.0] tracking-tight" delay={0.85} onMount />
        <p className="text-cream/85 text-lg md:text-xl mt-8 max-w-2xl mx-auto">
          We don't just help you move to Portugal. We help you build your future there — from your first bold decision to a business that grows across Europe.
        </p>
        <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
          <button data-testid="hero-cta" onClick={() => openConsultation()} className="group inline-flex items-center gap-3 rounded-full bg-gold px-9 py-4 font-600 text-maroon-deep hover:bg-gold-soft transition-all hover:scale-105 active:scale-95">
            Start Your Portugal Journey
            <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <a href="#roadmap" className="link-underline text-cream/80 hover:text-cream text-sm tracking-wide">See the 10-step roadmap</a>
        </div>
      </motion.div>

      <div className="absolute bottom-8 z-20 flex flex-col items-center text-cream/45">
        <span className="text-[10px] tracking-[0.3em] uppercase">Scroll to fly to Portugal</span>
        <ChevronDown className="h-5 w-5 mt-2 animate-bounce" />
      </div>
    </section>
  );
}
